import { Controller } from '@hotwired/stimulus'

// Toggles a collapsible content panel, mirroring Bootstrap's data-toggle="collapse".
// Nests safely without stopping propagation: the action is bound on the trigger itself, and a nested
// disclosure's trigger lives inside the ancestor's content, never inside the ancestor's trigger, so
// its clicks never reach the ancestor's handler. The click keeps bubbling to document, which is where
// dropdown_controller listens to close itself on an outside click.
//
// This sets ARIA state and `inert` (to keep nested links out of the tab order while collapsed), but
// renders no visibility itself. The content target's open/closed look (and the transition between
// them) comes from the `disclosure-content` utility (stylesheets/components.css), which animates
// height off the `aria-hidden` attribute this sets instead of snapping with `display:none`.
// The initial `aria-hidden`/`inert` must also be set server-side in the view to match the starting
// `expanded` value — this controller only updates them after connect.
//
// Targets:
//   trigger — the clickable element that toggles the panel
//   content — the panel that shows or hides
//
// Values:
//   expanded (Boolean, default: false) — whether the panel starts open
//
// Usage (nests to any depth, e.g. MUDANZAS → Países → Chile → Store → Categorías):
//   <div data-controller="disclosure" data-disclosure-expanded-value="true">
//     <button type="button"
//             data-disclosure-target="trigger"
//             data-action="click->disclosure#toggle"
//             aria-expanded="true"
//             aria-controls="paises-panel">Países</button>
//
//     <div id="paises-panel" data-disclosure-target="content" aria-hidden="false"
//          class="disclosure-content">
//       <div>  <%# single direct child = the grid row; keep it free of vertical margins %>
//         <div data-controller="disclosure">
//           <button type="button"
//                   data-disclosure-target="trigger"
//                   data-action="click->disclosure#toggle"
//                   aria-expanded="false"
//                   aria-controls="chile-panel">Chile</button>
//
//           <div id="chile-panel" data-disclosure-target="content" aria-hidden="true"
//                class="disclosure-content">
//             <div>...</div>
//           </div>
//         </div>
//       </div>
//     </div>
//   </div>
export default class extends Controller {
  static targets = ['trigger', 'content']
  static values = {
    expanded: { type: Boolean, default: false }
  }

  connect() {
    this.render()
  }

  toggle() {
    this.expandedValue = !this.expandedValue
    this.render()
  }

  render() {
    if (this.hasContentTarget) {
      this.contentTarget.setAttribute('aria-hidden', String(!this.expandedValue))
      this.contentTarget.toggleAttribute('inert', !this.expandedValue)
    }
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute('aria-expanded', String(this.expandedValue))
  }
}
